/**
 * useSettings.js
 *
 * React Query hooks for settings/configuration data.
 *
 * Usage:
 *   const { data: settings } = useAppSettings();
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingService } from 'services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────────────────
export const settingKeys = {
  app: ['settings', 'app'],
  branding: (host) => ['settings', 'branding', host],
};

// ─────────────────────────────────────────────────────────────────────────────
// QUERIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch global app settings.
 * Replaces: axios.get(`${api}setting`) in MyContextProvider.js
 */
export const useAppSettings = (options = {}) => {
  return useQuery({
    queryKey: settingKeys.app,
    queryFn: () => settingService.getSettings(),
    select: (data) => data?.setting ?? {},
    staleTime: 5 * 60 * 1000, // Settings rarely change — cache for 5 minutes
    ...options,
  });
};

/**
 * Fetch white-label branding data for a host.
 * Replaces: axios.post(`${api}branding`, { host_url }) in MyContextProvider.js
 *
 * @param {string} hostUrl
 * @param {boolean} isEnabled — only fetch for non-main servers
 */
export const useBranding = (hostUrl, isEnabled = true, options = {}) => {
  return useQuery({
    queryKey: settingKeys.branding(hostUrl),
    queryFn: () => settingService.getBranding(hostUrl),
    select: (data) => data?.data ?? {},
    enabled: !!hostUrl && isEnabled,
    staleTime: 10 * 60 * 1000, // Branding rarely changes
    ...options,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// MUTATIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Send email alert.
 */
export const useSendEmail = () => {
  return useMutation({
    mutationFn: ({ id, body }) => settingService.sendEmail(id, body),
  });
};

/**
 * Update maintenance config — auto-invalidates settings.
 */
export const useUpdateMaintenanceConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => settingService.updateMaintenanceConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingKeys.app });
    },
  });
};
